import { Loader2, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  notarialDocumentTypeTranslationKey,
  type NotarialCertificateLabelContext,
} from "@/lib/notarialDocumentTypeLabel";

/**
 * The only fields this table reads. Declared structurally rather than importing
 * either page's response type, because the consumer and the professional
 * receive the same attachment from two different endpoints — and naming one of
 * those types here would make the other surface convert to it for no reason.
 */
export interface QuoteAttachmentRow {
  id: string;
  originalFilename: string;
  documentType: string | null;
  createdAt: string;
}

export interface QuoteAttachmentsTableProps {
  attachments: QuoteAttachmentRow[];
  /** Decides whether a type label reads as notarial or accountant copy. */
  certificateLabelContext: NotarialCertificateLabelContext;
  onOpen: (attachmentId: string) => void;
  /**
   * The row currently being fetched, or null.
   *
   * Opening is NOT instant — the file is pulled through the API with the
   * caller's auth, buffered, and only then handed to the browser — so a row
   * with no feedback reads as a dead button on anything but a small file.
   */
  openingAttachmentId?: string | null;
  /**
   * Delete, or undefined for a read-only view.
   *
   * Undefined is the default BECAUSE the professional's view must never offer
   * it: these are the consumer's files, and the professional is reading them to
   * price a job. Making read-only the shape you get by omitting a prop means a
   * new caller cannot accidentally hand out a delete button.
   */
  onDelete?: (attachment: QuoteAttachmentRow) => void;
  /** Disables the delete affordance while a deletion is in flight. */
  isDeleting?: boolean;
  /**
   * Rendered instead of the table when there is nothing. Omit to render
   * nothing at all.
   *
   * A NODE, not a string. This table is shared by the consumer's detail page
   * and the professional's bidding page, and what "empty" means differs by
   * side and by whether the reader can still add anything — so the caller
   * owns that copy and its layout. A `string` prop forced one muted sentence
   * on every case.
   */
  empty?: ReactNode;
}

/**
 * The attachments a consumer sent with an RFQ, as a table.
 *
 * ONE component for both sides of the request. The consumer's detail page and
 * the professional's bidding page show the same rows, from the same upload, in
 * the same layout slot — but they had grown separate markup: a four-column
 * table here, a stack of clickable cards there. The card list had no date and,
 * more to the point, no per-row loading state, so the professional still had
 * the dead-button problem that was fixed for consumers.
 *
 * The type leads, deliberately, and the filename sits beside it rather than in
 * place of it: "IMG_2831.pdf" tells a professional nothing about what they are
 * pricing, while "Cédula de identidad" does. That was the one thing the card
 * list got right, and keeping the type in the first column preserves it.
 */
export function QuoteAttachmentsTable({
  attachments,
  certificateLabelContext,
  onOpen,
  openingAttachmentId = null,
  onDelete,
  isDeleting = false,
  empty,
}: QuoteAttachmentsTableProps): JSX.Element | null {
  const { t } = useTranslation();

  if (attachments.length === 0) {
    return empty != null ? <>{empty}</> : null;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("notary.requests.type")}</TableHead>
          <TableHead>{t("notary.requestDetail.documentName")}</TableHead>
          <TableHead>{t("notary.requestDetail.documentDate")}</TableHead>
          <TableHead className="w-[160px]">{t("requests.user.actions")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {attachments.map((attachment) => (
          <TableRow key={attachment.id}>
            <TableCell>
              {/* An untyped attachment gets a placeholder, NOT its filename:
                  the filename already has the next column, and repeating it
                  here would make the row look like it carries a type it does
                  not. The professional's old card list had no second column to
                  fall back on, which is why it did repeat it. */}
              {attachment.documentType ? (
                <Badge variant="secondary" className="font-normal normal-case">
                  {t(
                    notarialDocumentTypeTranslationKey(
                      attachment.documentType,
                      certificateLabelContext,
                    ),
                    attachment.documentType,
                  )}
                </Badge>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell className="max-w-[280px] truncate">
              {attachment.originalFilename}
            </TableCell>
            <TableCell>{formatDisplayDate(attachment.createdAt)}</TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onOpen(attachment.id)}
                  disabled={openingAttachmentId === attachment.id}
                >
                  {openingAttachmentId === attachment.id ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
                  ) : null}
                  {t("requests.user.quoteAttachmentOpen")}
                </Button>
                {onDelete ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(attachment)}
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
